import type { ConstValue } from '@bridge/types';

export const ENDORSEMENT_NAME = {
  Ars: 'ars',
  Base: 'base',
  BaseLimited: 'base_limited',
  Cards: 'cards',
  Cop: 'cop',
  CryptoToCrypto: 'crypto_to_crypto',
  CryptoToCryptoVolumeLimited: 'crypto_to_crypto_volume_limited',
  FasterPayments: 'faster_payments',
  Grandfathered: 'grandfathered',
  KycLite: 'kyc_lite',
  KycRelianceDataReceived: 'kyc_reliance_data_received',
  Mtl: 'mtl',
  Pix: 'pix',
  PixOfframp: 'pix_offramp',
  PixOnramp: 'pix_onramp',
  Reliance: 'reliance',
  Sepa: 'sepa',
  Spei: 'spei',
  Swift: 'swift',
} as const;

/** Type derived from ENDORSEMENT_NAME values */
export type EndorsementName = ConstValue<typeof ENDORSEMENT_NAME>;

export const ENDORSEMENT_NAME_WHITELIST = Object.values(ENDORSEMENT_NAME) as EndorsementName[];

export const PAYMENT_RAIL_ENDORSEMENTS = [
  ENDORSEMENT_NAME.Base,
  ENDORSEMENT_NAME.Cop,
  ENDORSEMENT_NAME.FasterPayments,
  ENDORSEMENT_NAME.Grandfathered,
  ENDORSEMENT_NAME.Pix,
  ENDORSEMENT_NAME.PixOfframp,
  ENDORSEMENT_NAME.PixOnramp,
  ENDORSEMENT_NAME.Sepa,
  ENDORSEMENT_NAME.Spei,
  ENDORSEMENT_NAME.Swift,
] as const;

export const OTHER_ENDORSEMENTS = [ENDORSEMENT_NAME.Cards, ENDORSEMENT_NAME.CryptoToCrypto] as const;

export const ENDORSEMENT_NAME_TO_FEATURE_ACCESS_LABEL: Record<EndorsementName, string> = {
  [ENDORSEMENT_NAME.Ars]: 'ARS',
  [ENDORSEMENT_NAME.Base]: 'US',
  [ENDORSEMENT_NAME.BaseLimited]: 'US (limited)',
  [ENDORSEMENT_NAME.Cards]: 'Cards',
  [ENDORSEMENT_NAME.Cop]: 'COP',
  [ENDORSEMENT_NAME.CryptoToCrypto]: 'Crypto to Crypto',
  [ENDORSEMENT_NAME.CryptoToCryptoVolumeLimited]: 'Crypto to Crypto (volume limited)',
  [ENDORSEMENT_NAME.FasterPayments]: 'Faster Payments',
  [ENDORSEMENT_NAME.Grandfathered]: 'US',
  [ENDORSEMENT_NAME.KycLite]: 'KYC Lite',
  [ENDORSEMENT_NAME.KycRelianceDataReceived]: 'Reliance Data Received',
  [ENDORSEMENT_NAME.Mtl]: 'MTL',
  [ENDORSEMENT_NAME.Pix]: 'PIX',
  [ENDORSEMENT_NAME.PixOfframp]: 'PIX Offramp',
  [ENDORSEMENT_NAME.PixOnramp]: 'PIX Onramp',
  [ENDORSEMENT_NAME.Reliance]: 'Reliance',
  [ENDORSEMENT_NAME.Sepa]: 'SEPA',
  [ENDORSEMENT_NAME.Spei]: 'SPEI',
  [ENDORSEMENT_NAME.Swift]: 'SWIFT',
};

export const ENDORSEMENT_NAME_TO_FEATURE_ACCESS_DESCRIPTION: Record<EndorsementName, string> = {
  [ENDORSEMENT_NAME.Ars]: 'Enables Argentina based transactions',
  [ENDORSEMENT_NAME.Base]: 'Enables US ACH, wire, and FedNow transactions',
  [ENDORSEMENT_NAME.BaseLimited]: 'Enables limited US ACH, wire, and FedNow transactions',
  [ENDORSEMENT_NAME.Cards]: 'Enables the issuing of a Bridge debit card to this customer',
  [ENDORSEMENT_NAME.Cop]: 'Enables Colombia based transactions',
  [ENDORSEMENT_NAME.CryptoToCrypto]: 'Enables crypto-to-crypto transactions',
  [ENDORSEMENT_NAME.CryptoToCryptoVolumeLimited]: 'Enables crypto-to-crypto transactions with volume limits',
  [ENDORSEMENT_NAME.FasterPayments]: 'Enables GBP Faster Payments transactions',
  [ENDORSEMENT_NAME.Grandfathered]: 'Enables US ACH, wire, and FedNow transactions',
  [ENDORSEMENT_NAME.KycLite]: 'Enables limited KYC verification for US-based individuals',
  [ENDORSEMENT_NAME.KycRelianceDataReceived]: 'Enables US ACH, wire, and FedNow transactions, wallets, and virtual accounts',
  [ENDORSEMENT_NAME.Mtl]: 'Enables money transmission under Bridge’s money transmitter license',
  [ENDORSEMENT_NAME.Pix]: 'Enables Brazil based transactions',
  [ENDORSEMENT_NAME.PixOfframp]: 'Enables Brazil based offramp transactions',
  [ENDORSEMENT_NAME.PixOnramp]: 'Enables Brazil based onramp transactions',
  [ENDORSEMENT_NAME.Reliance]: 'Customer was created under a reliance agreement with Bridge',
  [ENDORSEMENT_NAME.Sepa]: 'Enables EU-based transactions',
  [ENDORSEMENT_NAME.Spei]: 'Enables Mexico based transactions',
  [ENDORSEMENT_NAME.Swift]: 'Enables SWIFT international wire transactions',
};

export const ENDORSEMENT_REQUIREMENTS = {
  AddressOfIncorporation: 'address_of_incorporation',
  AddressOfOperation: 'address_of_operation',
  AddressOfResidence: 'address_of_residence',
  AssociatedPersonRelationship: 'associated_person_relationship',
  BusinessDescription: 'business_description',
  BusinessEin: 'business_ein',
  BusinessEinVerification: 'business_ein_verification',
  BusinessFormation: 'business_formation',
  BusinessIndustry: 'business_industry',
  BusinessLookup: 'business_lookup',
  BusinessName: 'business_name',
  BusinessOwnership: 'business_ownership',
  BusinessOwnershipDocuments: 'business_ownership_documents',
  BusinessProofOfAddress: 'business_proof_of_address',
  BusinessProofOfAddressManual: 'business_proof_of_address_manual',
  BusinessType: 'business_type',
  BusinessWebsite: 'business_website',
  DatabaseLookup: 'database_lookup',
  DateOfBirth: 'date_of_birth',
  EmailAddress: 'email_address',
  FirstName: 'first_name',
  GovernmentIdDocument: 'government_id_document',
  GovernmentIdVerification: 'government_id_verification',
  IndividualProofOfAddress: 'individual_proof_of_address',
  IsDao: 'is_dao',
  LastName: 'last_name',
  PendingRfi: 'pending_rfi',
  ProofOfAddress: 'proof_of_address',
  SourceOfFundsQuestionnaire: 'source_of_funds_questionnaire',
  TaxIdentificationNumber: 'tax_identification_number',
  TermsOfServiceV1: 'terms_of_service_v1',
  TermsOfServiceV2: 'terms_of_service_v2',
  TermsOfServiceV4: 'terms_of_service_v4',
} as const;

/** Type derived from ENDORSEMENT_REQUIREMENTS values */
export type EndorsementRequirement = ConstValue<typeof ENDORSEMENT_REQUIREMENTS>;
